<template>
  <div class="ci" :class="{ 'ci--noctx': !showCtx || !selected }">
    <!-- pane 1: conversations -->
    <aside class="ci__list" :class="{ 'ci__list--hidden': selectedId }">
      <div class="ci__listhead">
        <span class="ci__listtitle">Conversaciones<span v-if="openCount" class="ci__listcount">{{ openCount }}</span></span>
        <div class="ci__filters" role="tablist" aria-label="Filtrar conversaciones">
          <button
            v-for="f in filters"
            :key="f.value"
            type="button"
            role="tab"
            :aria-selected="String(statusFilter === f.value)"
            class="ci__filter"
            :class="{ 'ci__filter--on': statusFilter === f.value }"
            @click="setFilter(f.value)"
          >{{ f.label }}</button>
        </div>
      </div>
      <div v-if="conversations.length" class="ci__convos" role="list">
        <button
          v-for="c in conversations"
          :key="c.id"
          type="button"
          role="listitem"
          :aria-current="c.id === selectedId ? 'true' : 'false'"
          :aria-label="'Conversación con ' + nameOf(c)"
          class="ci__convo"
          :class="{ 'ci__convo--on': c.id === selectedId }"
          @click="select(c.id)"
        >
          <span class="ci__dot" :class="'ci__dot--' + tierClass(c)"></span>
          <span class="ci__convomain">
            <span class="ci__convotop">
              <span class="ci__convoname">{{ nameOf(c) }}</span>
              <span v-if="c.status === 'closed'" class="ci__closedtag">Cerrada</span>
              <span v-else-if="c.online" class="ci__online" title="En línea">●</span>
              <span class="ci__convotime">{{ ago(c.lastMessageAt) }}</span>
            </span>
            <span class="ci__convoprev">{{ c.lastMessagePreview || "—" }}</span>
          </span>
          <span v-if="c.agentUnread" class="ci__unread">{{ c.agentUnread }}</span>
        </button>
      </div>
      <div v-else class="ci__empty">No hay conversaciones abiertas. Iniciá una desde el tráfico en vivo.</div>
    </aside>

    <!-- pane 2: the chat -->
    <section class="ci__chat" :class="{ 'ci__chat--hidden': !selectedId }">
      <template v-if="selected">
        <header class="ci__chathead">
          <button type="button" class="ci__back" aria-label="Volver a la lista" @click="selectedId = null"><hero-icon :icon="ChevronLeftIcon" :size="18" /></button>
          <span class="ci__dot" :class="'ci__dot--' + tierClass(selected)"></span>
          <div class="ci__chatwho">
            <div class="ci__chatname">{{ nameOf(selected) }}<span v-if="selected.online" class="ci__online">● en línea</span></div>
            <div class="ci__chatsub">{{ selected.currentPage || (selected.online ? "Navegando" : "Desconectado") }}</div>
          </div>
          <button
            type="button"
            class="ci__iconbtn ci__ctxtoggle"
            :class="{ 'ci__iconbtn--on': showCtx }"
            :aria-pressed="String(showCtx)"
            aria-label="Mostrar u ocultar el contexto del visitante"
            title="Contexto del visitante"
            @click="showCtx = !showCtx"
          ><hero-icon :icon="InfoIcon" :size="17" /></button>
          <button v-if="selected.status !== 'closed'" type="button" class="ci__close" title="Cerrar conversación" @click="close(selected.id)">
            <hero-icon :icon="CloseIcon" :size="16" /> Cerrar
          </button>
          <button v-else type="button" class="ci__close ci__close--reopen" title="Reabrir conversación" @click="reopen(selected.id)">
            Reabrir
          </button>
        </header>

        <div
          ref="scroll"
          class="ci__messages"
          :class="{ 'ci__messages--drop': dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave.self="dragOver = false"
          @drop.prevent="onDrop"
        >
          <div v-if="loadingMsgs" class="ci__loading"><v-progress-circular indeterminate color="accent" size="26" width="3" /></div>
          <div
            v-for="m in messages"
            :key="m.id"
            class="ci__msg"
            :class="'ci__msg--' + m.sender"
          >
            <button v-if="m.property" type="button" class="ci__pcard" @click="openProperty(m.property)">
              <span class="ci__pcardimg" :style="cardImg(m.property)"><hero-icon v-if="!imgUrl(m.property)" :icon="HomeIcon" :size="20" class="ci__imgph" /></span>
              <span class="ci__pcardbody">
                <span class="ci__pcardtype">{{ typeLabelP(m.property.propertyType) }}</span>
                <span class="ci__pcardaddr">{{ m.property.fullAddress }}</span>
                <span class="ci__pcardprice">{{ m.property.price }}</span>
              </span>
              <hero-icon :icon="ChevronRightIcon" :size="16" class="ci__pcardchev" />
            </button>
            <div v-else class="ci__bubble">{{ m.body }}</div>
            <div v-if="m.property && m.body" class="ci__bubble ci__bubble--cap">{{ m.body }}</div>
            <div class="ci__msgtime">
              <span v-if="m.sender === 'visitor' && m.context" class="ci__msgctx">desde {{ m.context }} · </span>{{ time(m.createdAt) }}
            </div>
          </div>
          <div v-if="dragOver" class="ci__drophint">Soltá para compartir la propiedad</div>
        </div>

        <div v-if="tray.length" class="ci__tray">
          <div class="ci__traylist">
            <span v-for="p in tray" :key="p.id" class="ci__traychip">
              <span class="ci__traychipimg" :style="cardImg(p)"><hero-icon v-if="!imgUrl(p)" :icon="HomeIcon" :size="13" class="ci__imgph" /></span>
              <span class="ci__traychiptxt">{{ p.fullAddress }}</span>
              <button type="button" class="ci__traychipx" :aria-label="'Quitar ' + p.fullAddress" @click="removeFromTray(p.id)">
                <hero-icon :icon="CloseIcon" :size="13" />
              </button>
            </span>
          </div>
          <button type="button" class="ci__traysend" @click="sendTray">
            Enviar {{ tray.length }} {{ tray.length === 1 ? 'propiedad' : 'propiedades' }}
          </button>
        </div>

        <div v-if="selected.status !== 'closed'" class="ci__composer">
          <textarea
            v-model="draft"
            rows="1"
            placeholder="Escribí un mensaje…"
            aria-label="Escribir un mensaje"
            class="ci__input"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <button type="button" class="ci__send" :disabled="!draft.trim() || sending" aria-label="Enviar mensaje" @click="send">
            <hero-icon :icon="SendIcon" :size="18" />
          </button>
        </div>
        <div v-else class="ci__closedbar">Conversación cerrada · <button type="button" class="ci__reopenlink" @click="reopen(selected.id)">Reabrir</button></div>
      </template>
      <div v-else class="ci__pick">Elegí una conversación para responder.</div>
    </section>

    <!-- pane 3: visitor context + journey -->
    <aside v-if="selected && showCtx" class="ci__ctx">
      <div class="ci__ctxhead">
        <span class="ci__tier" :class="'ci__tier--' + tierClass(selected)">{{ tierLabel(selected) }}</span>
        <span class="ci__ctxname">{{ nameOf(selected) }}</span>
      </div>
      <div v-if="contactEmail(selected) || contactPhone(selected)" class="ci__ctxmeta">
        <div v-if="contactEmail(selected)">{{ contactEmail(selected) }}</div>
        <div v-if="contactPhone(selected)">{{ contactPhone(selected) }}</div>
        <div v-if="selected.guestName || selected.guestEmail || selected.guestPhone" class="ci__ctxgtag">Dejó su contacto en el chat</div>
        <div v-if="selected.inquiryId" class="ci__ctxgtag ci__ctxgtag--inq">Consulta vinculada #{{ selected.inquiryId }}</div>
      </div>
      <div class="ci__chips">
        <span v-if="selected.visitor && selected.visitor.topOperation" class="ci__chip ci__chip--op">{{ opLabel(selected.visitor.topOperation) }}</span>
        <span v-if="selected.visitor && selected.visitor.topZone" class="ci__chip ci__chip--zone">{{ selected.visitor.topZone }}</span>
        <span v-if="selected.visitor && selected.visitor.device" class="ci__chip">{{ deviceLabel(selected.visitor.device) }}</span>
        <span v-if="selected.online" class="ci__chip ci__chip--live">● {{ selected.currentPage || "En línea" }}</span>
      </div>

      <div class="ci__ctxtabs" role="tablist" aria-label="Contexto">
        <button type="button" role="tab" :aria-selected="String(ctxTab === 'journey')" class="ci__ctxtab" :class="{ 'ci__ctxtab--on': ctxTab === 'journey' }" @click="selectTab('journey')">Recorrido</button>
        <button type="button" role="tab" :aria-selected="String(ctxTab === 'props')" class="ci__ctxtab" :class="{ 'ci__ctxtab--on': ctxTab === 'props' }" @click="selectTab('props')">Propiedades</button>
      </div>

      <template v-if="ctxTab === 'journey'">
        <div v-if="journeyEvents.length" class="ci__journey">
          <div v-for="(e, i) in journeyEvents" :key="i" class="ci__jev" :class="'ci__jev--' + e.kind">
            <span class="ci__jdot"></span>
            <span class="ci__jbody">
              <span class="ci__jkind">{{ kindLabel(e.kind) }}</span>
              <span class="ci__jtitle">{{ e.kind === 'search' ? searchText(e.meta) : e.title }}</span>
              <span class="ci__jwhen">{{ time(e.at) }}</span>
            </span>
          </div>
        </div>
        <div v-else class="ci__ctxempty">Sin recorrido registrado.</div>
      </template>

      <template v-else>
        <input v-model="pickerQuery" type="search" class="ci__picksearch" placeholder="Buscar por dirección o tipo…" aria-label="Buscar propiedad" />
        <div class="ci__pickhint">Recomendadas por su recorrido · arrastrá al chat o tocá <hero-icon :icon="PlusIcon" :size="11" /> para sumar a la lista</div>
        <div v-if="filteredRecs.length" class="ci__picks">
          <div
            v-for="p in filteredRecs"
            :key="p.id"
            class="ci__pick"
            draggable="true"
            @dragstart="onCardDragStart($event, p)"
          >
            <span class="ci__pickimg" :style="cardImg(p)"><hero-icon v-if="!imgUrl(p)" :icon="HomeIcon" :size="20" class="ci__imgph" /></span>
            <span class="ci__pickbody">
              <span class="ci__picktype">
                <span class="ci__picktypetxt">{{ typeLabelP(p.propertyType) }}</span>
                <span v-if="p.trending" class="ci__picktrend">Muy buscada</span>
              </span>
              <span class="ci__pickaddr">{{ p.fullAddress }}</span>
              <span class="ci__pickprice">{{ p.price }}</span>
            </span>
            <button type="button" class="ci__pickshare" :aria-label="'Agregar ' + p.fullAddress" title="Agregar a la lista" @click="addToTray(p)">
              <hero-icon :icon="PlusIcon" :size="16" />
            </button>
          </div>
        </div>
        <div v-else class="ci__ctxempty">{{ recsLoading ? "Cargando recomendaciones…" : "Sin recomendaciones para este visitante." }}</div>
      </template>
    </aside>
  </div>
</template>

<script>
import { AgentConversations, AgentConversation, SendAgentMessage, MarkConversationRead, CloseConversation, ReopenConversation, ShareProperty, RecommendedForVisitor } from "@/graphql/queries/chat";
import { VisitorJourney } from "@/graphql/queries/analytics";

const AGENCY_IDS = ["1", "2"];
const API_ORIGIN = (process.env.VUE_APP_GRAPHQL_ENDPOINT || "").replace(/\/graphql.*$/, "");
import { subscribeAgentChat } from "@/services/chatCable";
import HeroIcon from "@/components/shared/HeroIcon";
import CloseIcon from "heroicons/24/outline/x-mark.svg";
import ChevronLeftIcon from "heroicons/24/outline/chevron-left.svg";
import SendIcon from "heroicons/24/outline/paper-airplane.svg";
import InfoIcon from "heroicons/24/outline/information-circle.svg";
import PlusIcon from "heroicons/24/outline/plus.svg";
import HomeIcon from "heroicons/24/outline/home-modern.svg";

const TIER_LABELS = { hot: "Caliente", warm: "Tibio", cold: "Frío" };
const OP_LABELS = { sell: "Venta", rent: "Alquiler" };
const DEV_LABELS = { mobile: "Móvil", tablet: "Tablet", desktop: "Escritorio" };

export default {
  components: { HeroIcon },

  data: () => ({
    CloseIcon, ChevronLeftIcon, SendIcon, InfoIcon, PlusIcon, HomeIcon,
    showCtx: true,
    tray: [],
    conversations: [],
    selectedId: null,
    messages: [],
    loadingMsgs: false,
    draft: "",
    sending: false,
    journey: null,
    ctxTab: "journey",       // journey | props
    recs: [],
    recsLoading: false,
    recsForId: null,
    pickerQuery: "",
    dragOver: false,
    statusFilter: "open",
    filters: [
      { value: "open", label: "Abiertas" },
      { value: "closed", label: "Cerradas" },
      { value: "all", label: "Todas" }
    ],
    _sub: null,
    _presTimer: null
  }),

  computed: {
    selected() {
      return this.conversations.find(c => c.id === this.selectedId) || null;
    },
    openCount() {
      return this.conversations.length;
    },
    journeyEvents() {
      const s = this.journey && this.journey.sessions;
      if (s && s.length) return (s[0].events || []).slice(0, 8);
      return (this.journey && this.journey.events ? this.journey.events : []).slice(0, 8);
    },
    filteredRecs() {
      const q = this.pickerQuery.trim().toLowerCase();
      if (!q) return this.recs;
      return this.recs.filter(p =>
        (p.fullAddress || "").toLowerCase().includes(q) ||
        (p.propertyType || "").toLowerCase().includes(q)
      );
    }
  },

  created() {
    this.fetchList();
    this._sub = subscribeAgentChat(this.onCable);
    // flip visitors to "offline" when their presence heartbeats stop arriving
    this._presTimer = setInterval(this.sweepPresence, 5000);
  },
  beforeDestroy() {
    if (this._sub) { try { this._sub.unsubscribe(); } catch (e) { /* ignore */ } }
    clearInterval(this._presTimer);
  },

  methods: {
    fetchList() {
      this.$apollo
        .query({ query: AgentConversations, fetchPolicy: "no-cache", variables: { status: this.statusFilter } })
        .then(res => {
          const now = Date.now();
          this.conversations = (res.data.agentConversations || []).map(c => {
            if (c.online) c._presenceAt = now; // start the offline countdown
            return c;
          });
        })
        .catch(() => {});
    },

    setFilter(value) {
      if (this.statusFilter === value) return;
      this.statusFilter = value;
      this.fetchList();
    },

    // called from Live.vue after a nudge — upsert + open it
    open(convo) {
      if (!convo) return;
      this.tray = [];
      if (convo.online) convo._presenceAt = Date.now();
      this.upsert(convo);
      this.selectedId = convo.id;
      this.messages = (convo.messages || []).slice();
      this.loadJourney(convo);
      if (this.ctxTab === "props") this.loadRecs(convo);
      this.$nextTick(this.scrollDown);
    },

    select(id) {
      this.selectedId = id;
      this.messages = [];
      this.tray = [];
      this.loadingMsgs = true;
      this.$apollo
        .query({ query: AgentConversation, fetchPolicy: "no-cache", variables: { id } })
        .then(res => {
          const c = res.data.agentConversation;
          this.loadingMsgs = false;
          if (!c) return;
          if (c.online) c._presenceAt = Date.now();
          this.upsert(c);
          this.messages = (c.messages || []).slice();
          this.loadJourney(c);
          if (this.ctxTab === "props") this.loadRecs(c);
          this.$nextTick(this.scrollDown);
        })
        .catch(() => { this.loadingMsgs = false; });
      this.markRead(id);
    },

    markRead(id) {
      const c = this.conversations.find(x => x.id === id);
      if (c) c.agentUnread = 0;
      this.$apollo.mutate({ mutation: MarkConversationRead, variables: { conversationId: id } }).catch(() => {});
    },

    loadJourney(convo) {
      this.journey = null;
      const vid = convo.visitor && convo.visitor.id;
      if (!vid) return;
      this.$apollo
        .query({ query: VisitorJourney, fetchPolicy: "no-cache", variables: { visitorId: vid, limit: 60 } })
        .then(res => { this.journey = res.data.visitorJourney || null; })
        .catch(() => {});
    },

    // ---- property picker (journey-based recommendations) ----
    selectTab(tab) {
      this.ctxTab = tab;
      if (tab === "props" && this.selected && this.recsForId !== this.selectedId) this.loadRecs(this.selected);
    },
    loadRecs(convo) {
      if (!convo || !convo.visitorToken) { this.recs = []; return; }
      this.recsForId = convo.id;
      this.recsLoading = true;
      this.$apollo
        .query({ query: RecommendedForVisitor, fetchPolicy: "no-cache", variables: { visitorToken: convo.visitorToken, agencyIds: AGENCY_IDS, limit: 12 } })
        .then(res => { this.recs = (res.data.recommendedForVisitor || []).slice(); this.recsLoading = false; })
        .catch(() => { this.recsLoading = false; });
    },
    // staging list: drop/tap collects properties; the agent sends them together
    addToTray(p) {
      if (!p || this.tray.some(x => String(x.id) === String(p.id))) return;
      this.tray.push({ id: p.id, fullAddress: p.fullAddress, propertyType: p.propertyType, price: p.price, coverImage: p.coverImage, images: p.images });
    },
    removeFromTray(id) { this.tray = this.tray.filter(x => String(x.id) !== String(id)); },
    sendTray() {
      const items = this.tray.slice();
      this.tray = [];
      items.forEach(p => this.shareById(p.id));
    },
    shareById(id) {
      if (!id || !this.selectedId) return;
      this.$apollo
        .mutate({ mutation: ShareProperty, variables: { conversationId: this.selectedId, propertyId: String(id) } })
        .then(res => {
          const m = res.data.shareProperty && res.data.shareProperty.message;
          if (m) this.appendMessage(this.selectedId, m);
        })
        .catch(() => {});
    },
    onCardDragStart(e, p) {
      try { e.dataTransfer.setData("text/plain", String(p.id)); e.dataTransfer.effectAllowed = "copy"; } catch (err) { /* ignore */ }
    },
    onDrop(e) {
      this.dragOver = false;
      let id = null;
      try { id = e.dataTransfer.getData("text/plain"); } catch (err) { /* ignore */ }
      if (!id) return;
      const p = this.recs.find(x => String(x.id) === String(id));
      this.addToTray(p || { id, fullAddress: "Propiedad" });
    },
    openProperty(p) {
      if (!p) return;
      this.$emit("open-property", { id: p.id, fullAddress: p.fullAddress, propertyType: p.propertyType });
    },
    imgUrl(p) {
      const raw = (p && (p.coverImage || (p.images && p.images[0] && (p.images[0].smallUrl || p.images[0].thumbUrl)))) || "";
      if (!raw) return "";
      return /^https?:\/\//.test(raw) ? raw : API_ORIGIN + raw;
    },
    cardImg(p) {
      const u = this.imgUrl(p);
      return u ? { backgroundImage: 'url("' + u + '")' } : {};
    },
    typeLabelP(t) {
      if (!t) return "Propiedad";
      const key = "property.type." + String(t).toLowerCase();
      const label = this.$t(key);
      return label === key ? t : label;
    },

    send() {
      const body = this.draft.trim();
      if (!body || this.sending || !this.selectedId) return;
      this.sending = true;
      const id = this.selectedId;
      this.$apollo
        .mutate({ mutation: SendAgentMessage, variables: { conversationId: id, body } })
        .then(res => {
          this.sending = false;
          const m = res.data.sendAgentMessage && res.data.sendAgentMessage.message;
          if (m) this.appendMessage(id, m);
          this.draft = "";
        })
        .catch(() => { this.sending = false; });
    },

    // closing keeps the conversation (as history) — just flips its status
    close(id) {
      this.$apollo.mutate({ mutation: CloseConversation, variables: { conversationId: id } }).catch(() => {});
      const c = this.conversations.find(x => x.id === id);
      if (c) { this.$set(c, "status", "closed"); c.online = false; }
    },
    reopen(id) {
      this.$apollo.mutate({ mutation: ReopenConversation, variables: { conversationId: id } }).catch(() => {});
      const c = this.conversations.find(x => x.id === id);
      if (c) this.$set(c, "status", "open");
    },

    // ---- realtime ----
    onCable(data) {
      if (!data) return;
      const cid = String(data.conversationId);
      if (data.type === "message" || data.type === "nudge") {
        const c = this.conversations.find(x => String(x.id) === cid);
        if (c) {
          if (data.message) c.lastMessagePreview = data.message.body;
          c.lastMessageAt = data.message ? data.message.createdAt : c.lastMessageAt;
          if (typeof data.agentUnread === "number" && String(this.selectedId) !== cid) c.agentUnread = data.agentUnread;
          if (data.message && data.message.sender === "visitor") { c.online = true; c._presenceAt = Date.now(); }
          this.bump(c.id);
        } else {
          this.fetchList(); // new conversation from another seat
        }
        if (String(this.selectedId) === cid && data.message) this.appendMessage(cid, data.message);
      } else if (data.type === "presence") {
        // live location: match by visitor token (works even before we know the id)
        const c = this.conversations.find(x => x.visitorToken === data.visitorToken);
        if (c) {
          const moved = c.currentPage !== data.page;
          c.online = true;
          this.$set(c, "currentPage", data.page);
          c._presenceAt = Date.now();
          // they navigated while we're watching them → refresh the journey pane
          if (moved && String(this.selectedId) === String(c.id)) this.loadJourney(c);
        }
      } else if (data.type === "read") {
        const c = this.conversations.find(x => String(x.id) === cid);
        if (c && typeof data.agentUnread === "number") c.agentUnread = data.agentUnread;
      } else if (data.type === "status") {
        const c = this.conversations.find(x => String(x.id) === cid);
        if (c) this.$set(c, "status", data.status); // keep it — just reflect open/closed
      } else if (data.type === "contact") {
        const c = this.conversations.find(x => String(x.id) === cid);
        if (c) {
          this.$set(c, "guestName", data.guestName);
          this.$set(c, "guestEmail", data.guestEmail);
          this.$set(c, "guestPhone", data.guestPhone);
        }
      }
    },

    // no heartbeat for ~50s → visitor left; show them offline
    sweepPresence() {
      const now = Date.now();
      this.conversations.forEach(c => {
        if (c.online && c._presenceAt && now - c._presenceAt > 50000) c.online = false;
      });
    },

    appendMessage(cid, m) {
      if (String(this.selectedId) !== String(cid)) return;
      if (this.messages.some(x => String(x.id) === String(m.id))) return; // de-dupe (mutation + cable)
      this.messages.push(m);
      this.$nextTick(this.scrollDown);
    },
    upsert(convo) {
      const i = this.conversations.findIndex(c => String(c.id) === String(convo.id));
      if (i === -1) this.conversations.unshift(convo);
      else this.$set(this.conversations, i, Object.assign({}, this.conversations[i], convo));
    },
    bump(id) {
      const i = this.conversations.findIndex(c => c.id === id);
      if (i > 0) { const [c] = this.conversations.splice(i, 1); this.conversations.unshift(c); }
    },
    scrollDown() {
      const el = this.$refs.scroll;
      if (el) el.scrollTop = el.scrollHeight;
    },

    // ---- labels ----
    nameOf(c) {
      return (c.visitor && (c.visitor.contactName || c.visitor.contactEmail)) || c.guestName || c.guestEmail || "Visitante anónimo";
    },
    contactEmail(c) {
      return (c.visitor && c.visitor.contactEmail) || c.guestEmail || "";
    },
    contactPhone(c) {
      return (c.visitor && c.visitor.contactPhone) || c.guestPhone || "";
    },
    tierClass(c) {
      return (c.visitor && c.visitor.leadTier) || "anon";
    },
    tierLabel(c) {
      const t = c.visitor && c.visitor.leadTier;
      return TIER_LABELS[t] || "Sin puntaje";
    },
    opLabel(o) { return OP_LABELS[o] || o; },
    deviceLabel(d) { return DEV_LABELS[d] || d; },
    kindLabel(k) { return { search: "Búsqueda", visit: "Visita", inquiry: "Consulta" }[k] || k; },
    searchText(meta) {
      if (!meta) return "Todas las propiedades";
      const p = [];
      if (meta.operation) p.push(this.opLabel(meta.operation));
      (meta.types || []).forEach(t => p.push(t));
      if (meta.zone) p.push(meta.zone);
      return p.join(" · ") || "Todas las propiedades";
    },
    ago(s) {
      if (!s) return "";
      const secs = Math.floor((Date.now() - new Date(s).getTime()) / 1000);
      if (secs < 60) return "ahora";
      if (secs < 3600) return `${Math.floor(secs / 60)}m`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
      return `${Math.floor(secs / 86400)}d`;
    },
    time(s) {
      if (!s) return "";
      try { return new Date(s).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; }
    }
  }
};
</script>

<style scoped>
.ci {
  display: grid; grid-template-columns: 260px 1fr 300px; gap: 0;
  height: calc(100vh - 196px); min-height: 520px;
  background: #fff; border: 1px solid #ece7de; border-radius: 14px; overflow: hidden;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
}
.ci--noctx { grid-template-columns: 260px 1fr; }
@media (max-width: 1024px) { .ci, .ci--noctx { grid-template-columns: 260px 1fr; } .ci__ctx { display: none; } }
@media (max-width: 700px) {
  .ci, .ci--noctx { grid-template-columns: 1fr; }
  .ci__list--hidden { display: none; }
  .ci__chat--hidden { display: none; }
}

/* pane 1 */
.ci__list { border-right: 1px solid #ece7de; display: flex; flex-direction: column; min-height: 0; }
.ci__listhead { padding: 11px 12px 9px; border-bottom: 1px solid #f1ece3; }
.ci__listtitle { display: block; font-size: 10px; letter-spacing: .1em; text-transform: uppercase; font-weight: 700; color: #a39a8c; margin-bottom: 8px; }
.ci__listcount { color: #b52118; margin-left: 5px; }
.ci__filters { display: flex; gap: 3px; background: #f4f0e8; border-radius: 8px; padding: 2px; }
.ci__filter { flex: 1; border: none; background: transparent; color: #756f64; font: inherit; font-size: 11px; font-weight: 600; border-radius: 6px; padding: 5px 0; cursor: pointer; transition: background .12s, color .12s; }
.ci__filter--on { background: #fff; color: #1a1814; box-shadow: 0 1px 3px -1px rgba(40, 33, 22, .25); }
.ci__filter:focus-visible { outline: 2px solid #b52118; outline-offset: -1px; }
.ci__convos { overflow-y: auto; min-height: 0; }
.ci__closedtag { flex: none; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #a39a8c; background: #f1ece3; padding: 1px 6px; border-radius: 999px; }
.ci__convo {
  display: flex; align-items: flex-start; gap: 10px; width: 100%; text-align: left;
  padding: 12px 15px; background: none; border: none; border-bottom: 1px solid #f4f0e8; cursor: pointer; font: inherit;
}
.ci__convo:hover { background: #faf8f4; }
.ci__convo--on { background: #f7f5f1; }
.ci__convomain { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.ci__convotop { display: flex; align-items: baseline; gap: 6px; }
.ci__convoname { font-size: 13.5px; font-weight: 600; color: #1a1814; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__convotime { margin-left: auto; flex: none; font-size: 11px; color: #a39a8c; }
.ci__convoprev { font-size: 12px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__unread { flex: none; align-self: center; background: #b52118; color: #fff; font-size: 11px; font-weight: 700; border-radius: 999px; min-width: 18px; height: 18px; padding: 0 5px; display: flex; align-items: center; justify-content: center; }

.ci__dot { flex: none; width: 9px; height: 9px; border-radius: 50%; margin-top: 4px; background: #a39a8c; }
.ci__dot--hot { background: #b52118; }
.ci__dot--warm { background: #d8941f; }
.ci__dot--cold { background: #5a6b7a; }
.ci__dot--anon { background: #c2bcb0; }
.ci__online { color: #2c9a4a; font-size: 10px; }

/* pane 2 */
.ci__chat { display: flex; flex-direction: column; min-height: 0; }
.ci__chathead { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-bottom: 1px solid #f1ece3; }
.ci__back { display: none; background: none; border: none; cursor: pointer; color: #756f64; }
@media (max-width: 700px) { .ci__back { display: inline-flex; } }
.ci__chatwho { flex: 1 1 auto; min-width: 0; }
.ci__chatname { font-size: 15px; font-weight: 700; color: #1a1814; display: flex; align-items: center; gap: 8px; }
.ci__chatname .ci__online { font-size: 11px; font-weight: 600; }
.ci__chatsub { font-size: 12px; color: #a39a8c; }
.ci__iconbtn { flex: none; display: inline-flex; align-items: center; justify-content: center; width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e3ddd2; background: #fff; color: #756f64; cursor: pointer; transition: background .12s, color .12s, border-color .12s; }
.ci__iconbtn:hover { background: #f4f0e8; }
.ci__iconbtn--on { background: #1a1814; border-color: #1a1814; color: #fff; }
.ci__close { flex: none; display: inline-flex; align-items: center; gap: 5px; background: #fff; border: 1px solid #e3ddd2; border-radius: 8px; padding: 6px 11px; font-size: 12px; font-weight: 600; color: #756f64; cursor: pointer; }
.ci__close:hover { background: #f4f0e8; }
/* keyboard focus (accessibility) */
.ci__convo:focus-visible { outline: 2px solid #b52118; outline-offset: -2px; }
.ci__send:focus-visible, .ci__close:focus-visible, .ci__back:focus-visible, .ci__iconbtn:focus-visible, .ci__reopenlink:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }
.ci__input:focus-visible { outline: none; }
.ci__close--reopen { color: #b52118; border-color: #f1c9c5; background: #fffaf9; font-weight: 700; }
.ci__close--reopen:hover { background: #fdecea; }
.ci__closedbar { padding: 14px; text-align: center; font-size: 12.5px; color: #a39a8c; border-top: 1px solid #f1ece3; background: #faf8f4; }
.ci__reopenlink { background: none; border: none; color: #b52118; font: inherit; font-weight: 700; cursor: pointer; padding: 0; }
.ci__reopenlink:hover { text-decoration: underline; }

.ci__messages { flex: 1 1 auto; overflow-y: auto; min-height: 0; padding: 16px; display: flex; flex-direction: column; gap: 10px; background: #faf8f4; }
.ci__msg { display: flex; flex-direction: column; max-width: 78%; }
.ci__msg--agent { align-self: flex-end; align-items: flex-end; }
.ci__msg--visitor { align-self: flex-start; align-items: flex-start; }
.ci__msg--system { align-self: center; align-items: center; max-width: 90%; }
.ci__bubble { padding: 9px 13px; border-radius: 13px; font-size: 14px; line-height: 1.4; white-space: pre-wrap; word-break: break-word; }
.ci__msg--agent .ci__bubble { background: #b52118; color: #fff; border-bottom-right-radius: 4px; }
.ci__msg--visitor .ci__bubble { background: #fff; color: #1a1814; border: 1px solid #ece7de; border-bottom-left-radius: 4px; }
.ci__msg--system .ci__bubble { background: #f1ece3; color: #756f64; font-size: 12.5px; }
.ci__msgtime { font-size: 10.5px; color: #a39a8c; margin-top: 3px; }
.ci__msgctx { color: #b58a3c; font-weight: 600; }

.ci__composer { display: flex; align-items: flex-end; gap: 8px; padding: 12px 14px; border-top: 1px solid #f1ece3; }
.ci__input { flex: 1 1 auto; resize: none; max-height: 120px; border: 1px solid #e3ddd2; border-radius: 10px; padding: 10px 12px; font: inherit; font-size: 14px; color: #1a1814; outline: none; }
.ci__input:focus { border-color: #b52118; }
.ci__send { flex: none; width: 40px; height: 40px; border-radius: 10px; background: #b52118; color: #fff; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.ci__send:disabled { background: #d9cfc0; cursor: default; }
.ci__send:not(:disabled):hover { background: #9d1d15; }

.ci__pick, .ci__empty, .ci__ctxempty { color: #a39a8c; font-size: 13.5px; text-align: center; padding: 40px 24px; }
.ci__loading { display: flex; justify-content: center; padding: 24px; }

/* pane 3 */
.ci__ctx { border-left: 1px solid #ece7de; padding: 16px; overflow-y: auto; min-height: 0; }
.ci__ctxhead { display: flex; align-items: center; gap: 8px; }
.ci__ctxname { font-size: 15px; font-weight: 700; color: #1a1814; }
.ci__tier { flex: none; font-size: 9.5px; font-weight: 700; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
.ci__tier--hot { background: #fdecea; color: #b52118; }
.ci__tier--warm { background: #fdeecf; color: #8a5a00; }
.ci__tier--cold { background: #eef1f4; color: #6a727b; }
.ci__tier--anon { background: #f1ece3; color: #a39a8c; }
.ci__ctxmeta { font-size: 12.5px; color: #756f64; margin-top: 8px; }
.ci__ctxgtag { display: inline-block; margin-top: 5px; margin-right: 5px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #2c6e49; background: #e3f3e8; padding: 2px 8px; border-radius: 999px; }
.ci__ctxgtag--inq { color: #8a5a00; background: #fdeecf; }
.ci__chips { display: flex; flex-wrap: wrap; gap: 6px; margin: 12px 0 16px; }
.ci__chip { font-size: 11px; font-weight: 600; background: #f1ece3; color: #6a645a; padding: 2px 9px; border-radius: 999px; }
.ci__chip--op { background: #fdeecf; color: #8a5a00; }
.ci__chip--zone { background: #e1ecff; color: #2353b8; }
.ci__chip--live { background: #fdecea; color: #b52118; }
.ci__ctxlabel { font-size: 10px; letter-spacing: .1em; text-transform: uppercase; font-weight: 700; color: #a39a8c; margin-bottom: 10px; padding-top: 6px; border-top: 1px solid #f1ece3; }
.ci__journey { display: flex; flex-direction: column; gap: 0; }
.ci__jev { display: flex; gap: 10px; padding: 7px 0; }
.ci__jdot { flex: none; width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; background: #756f64; }
.ci__jev--search .ci__jdot { background: #b52118; }
.ci__jev--visit .ci__jdot { background: #c89b3c; }
.ci__jev--inquiry .ci__jdot { background: #2c6e49; }
.ci__jbody { display: flex; flex-direction: column; min-width: 0; }
.ci__jkind { font-size: 9.5px; letter-spacing: .08em; text-transform: uppercase; font-weight: 700; color: #a39a8c; }
.ci__jtitle { font-size: 12.5px; color: #1a1814; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__jwhen { font-size: 11px; color: #a39a8c; }

/* context tabs */
.ci__ctxtabs { display: flex; gap: 3px; background: #f4f0e8; border-radius: 8px; padding: 2px; margin: 4px 0 12px; }
.ci__ctxtab { flex: 1; border: none; background: transparent; color: #756f64; font: inherit; font-size: 11.5px; font-weight: 600; border-radius: 6px; padding: 6px 0; cursor: pointer; }
.ci__ctxtab--on { background: #fff; color: #1a1814; box-shadow: 0 1px 3px -1px rgba(40, 33, 22, .25); }
.ci__ctxtab:focus-visible { outline: 2px solid #b52118; outline-offset: -1px; }

/* property picker */
.ci__picksearch { width: 100%; border: 1px solid #e3ddd2; border-radius: 9px; padding: 8px 11px; font: inherit; font-size: 13px; color: #1a1814; outline: none; margin-bottom: 8px; }
.ci__picksearch:focus { border-color: #b52118; }
.ci__pickhint { font-size: 11px; color: #a39a8c; margin-bottom: 12px; display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.ci__picks { display: flex; flex-direction: column; gap: 8px; }
.ci__pick { display: flex; gap: 10px; padding: 8px; border: 1px solid #ece7de; border-radius: 11px; background: #fff; cursor: grab; transition: border-color .15s, box-shadow .15s, transform .15s; }
.ci__pick:hover { border-color: #dcd4c6; box-shadow: 0 8px 20px -16px rgba(40, 33, 22, .3); transform: translateY(-1px); }
.ci__pick:active { cursor: grabbing; }
.ci__pickimg { flex: none; width: 56px; height: 56px; border-radius: 8px; background: #f1ece3 center/cover no-repeat; }
.ci__pickbody { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.ci__picktype { display: flex; align-items: center; gap: 6px; min-width: 0; }
.ci__picktypetxt { font-size: 12px; font-weight: 700; color: #1a1814; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__picktrend { flex: none; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: .02em; color: #8a5a00; background: #fdeecf; padding: 2px 6px; border-radius: 999px; white-space: nowrap; line-height: 1.2; }
.ci__pickaddr { font-size: 11.5px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__pickprice { font-size: 12.5px; font-weight: 700; color: #b52118; margin-top: 1px; }
.ci__pickshare { flex: none; align-self: center; width: 30px; height: 30px; border-radius: 8px; border: 1px solid #f1c9c5; background: #fffaf9; color: #b52118; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.ci__pickshare:hover { background: #fdecea; }
.ci__pickshare:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }

/* shared property card in a message */
.ci__messages--drop { outline: 2px dashed #b52118; outline-offset: -6px; background: #fdf7f6; }
.ci__drophint { align-self: center; margin: 6px auto; font-size: 12.5px; font-weight: 700; color: #b52118; background: #fdecea; padding: 6px 14px; border-radius: 999px; }
.ci__pcard { display: flex; align-items: center; gap: 12px; width: 320px; max-width: 100%; text-align: left; background: #fff; border: 1px solid #ece7de; border-radius: 12px; cursor: pointer; padding: 9px 11px; font: inherit; transition: border-color .15s, box-shadow .15s, transform .15s; }
.ci__pcard:hover { border-color: #dcd4c6; box-shadow: 0 8px 20px -16px rgba(40, 33, 22, .3); transform: translateY(-1px); }
.ci__pcard:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }
.ci__pcardimg { flex: none; width: 56px; height: 56px; border-radius: 8px; background: #f1ece3 center/cover no-repeat; }
.ci__pcardbody { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.ci__pcardtype { font-size: 10px; letter-spacing: .12em; text-transform: uppercase; font-weight: 600; color: #a39a8c; }
.ci__pcardaddr { font-size: 14px; font-weight: 600; color: #1a1814; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__pcardprice { font-size: 13.5px; font-weight: 600; color: #1a1814; margin-top: 1px; }
.ci__pcardchev { flex: none; color: #cdc4b5; }
.ci__bubble--cap { margin-top: 5px; }

/* image placeholder (missing cover) */
.ci__pcardimg, .ci__pickimg, .ci__traychipimg { display: flex; align-items: center; justify-content: center; }
.ci__imgph { color: #cdc4b5; }

/* staging tray */
.ci__tray { border-top: 1px solid #f1ece3; padding: 10px 14px; background: #faf8f4; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ci__traylist { display: flex; gap: 6px; flex: 1 1 auto; min-width: 0; overflow-x: auto; }
.ci__traychip { display: inline-flex; align-items: center; gap: 6px; flex: none; max-width: 190px; background: #fff; border: 1px solid #ece7de; border-radius: 999px; padding: 3px 4px 3px 3px; }
.ci__traychipimg { flex: none; width: 24px; height: 24px; border-radius: 999px; background: #f1ece3 center/cover no-repeat; }
.ci__traychiptxt { font-size: 11.5px; font-weight: 600; color: #56504a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ci__traychipx { flex: none; display: inline-flex; width: 18px; height: 18px; border: none; background: none; color: #a39a8c; cursor: pointer; padding: 0; align-items: center; justify-content: center; }
.ci__traychipx:hover { color: #b52118; }
.ci__traysend { flex: none; background: #b52118; color: #fff; border: none; border-radius: 8px; padding: 8px 14px; font-size: 12.5px; font-weight: 700; cursor: pointer; }
.ci__traysend:hover { background: #9d1d15; }
.ci__traysend:focus-visible { outline: 2px solid #b52118; outline-offset: 2px; }
</style>
